import { useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  DocumentCheckIcon,
  BuildingLibraryIcon,
  MapIcon,
  ClipboardDocumentCheckIcon,
  EnvelopeIcon,
  FingerPrintIcon,
  ScaleIcon,
  ArrowPathIcon,
  DocumentMagnifyingGlassIcon,
  BanknotesIcon,
  MegaphoneIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';

/* ============================================================
   Advanced Business Solutions × Clarid — partnership page.

   REBUILT 2026-09-30 after the full-team meeting. The first
   version was written for a single consultant (a CPA) ahead of
   one conversation, so it led with the practitioner case and
   organized capability by engagement type to route around an
   unknown service line.

   That framing is now retired. The audience is the whole ABS
   team — mixed roles, already met — so the page leads with
   WHAT CLARID DOES, stated plainly and by area, and closes on
   HOW WE COULD WORK TOGETHER. Those two are the spine.

   NO ABS INDIVIDUALS ARE NAMED anywhere on this page, per
   Bobby. The two people named are Clarid's own partners, whose
   credentials now get a section of their own:
     · Jason Willard  — former FDIC Supervisory Examiner
     · Michael Wallace — former Federal Reserve Supervisory
       Examiner, fair lending SME, Wallace Consulting Co., LLC
   Both sourced from our own DD and RFI materials
   (docs/CROWE_RFI_RESPONSE.md, docs/DUE_DILIGENCE.md), not
   from recollection.

   STILL DELIBERATELY EXCLUDED: pricing, the asset band (see
   feedback_positioning), and anything inferred about ABS's
   practice depth. Their site carries identical compliance and
   CRA blurbs — almost certainly a copy-paste error — so
   neither is quoted back. Alvarez & Marsal and Duane Morris
   both shipped factual errors about the partner by inferring
   from marketing copy; nothing here is inferred.

   SHIPPED vs ROADMAP is marked explicitly in the capability
   section. A firm that does audit work will check.
   ============================================================ */

/* ─── Animation helpers ─── */
const ease = [0.44, 0, 0.56, 1] as const;

function FadeUp({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 40 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay, ease }} className={className}>
      {children}
    </motion.div>
  );
}

function FadeIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0 }} animate={isInView ? { opacity: 1 } : {}} transition={{ duration: 0.7, delay, ease }} className={className}>
      {children}
    </motion.div>
  );
}

function ScaleIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, scale: 0.95 }} animate={isInView ? { opacity: 1, scale: 1 } : {}} transition={{ duration: 0.6, delay, ease }} className={className}>
      {children}
    </motion.div>
  );
}

/* ─── Data ─── */

/** What Clarid does, by area. Every line here is running in production today. */
const capabilities = [
  {
    icon: DocumentCheckIcon,
    area: 'HMDA',
    title: 'The LAR arrives already validated',
    description:
      '170+ edit checks across validity, quality, macro-quality, and syntactical — each finding explained in plain language rather than an edit code. ML-assisted column mapping pulls the file straight out of the LOS, with geocoding and data-quality scoring on arrival. The fair lending screens run against that same validated file, so the review starts from a clean record instead of producing one.',
  },
  {
    icon: MapIcon,
    area: 'Fair lending',
    title: 'Underwriting, pricing, and redlining',
    description:
      'Denial-rate and pricing-disparity testing, logistic and OLS regression, and matched-pair file review — alongside redlining analysis with Census tract mapping, demographic overlays, branch and office distribution against that geography, and majority-minority tract concentration. Screens run per assessment area and per year, with Census vintages matched to the year under review rather than to the latest data.',
  },
  {
    icon: BuildingLibraryIcon,
    area: 'CRA',
    title: 'Assessment areas through performance evaluation',
    description:
      'Assessment-area construction resolved to the correct OMB delineation for each analysis year, geographic and borrower lending distribution, small business and community development analysis, and peer benchmarking drawn from FDIC Call Report data — assembled into performance-evaluation tables for small, intermediate, and large-bank exams.',
  },
  {
    icon: BanknotesIcon,
    area: 'CRA investment test',
    title: 'Bond-level community development validation',
    description:
      'Agency mortgage-backed securities validated for CRA investment credit — geocoded to the buying institution’s assessment areas, with affordability re-derived independently from Census reference data rather than accepted from the party selling the bond. Both in-assessment-area and statewide qualifying dollars are reported, against the census vintage for the year the pool was securitized.',
  },
  {
    icon: MegaphoneIcon,
    area: 'Marketing',
    title: 'Review before it goes out, not after',
    description:
      'Marketing-compliance review and program and policy management, kept current against regulatory change — the part of the record examiners increasingly ask for and institutions most often cannot produce on demand.',
  },
  {
    icon: ClipboardDocumentCheckIcon,
    area: 'Packaging',
    title: 'One record, not a folder of reports',
    description:
      'Findings from marketing, HMDA, fair lending, and CRA assemble into a single exam-ready package and a board-ready summary — cross-referenced, timestamped, and hash-verified. The institution receives something it can hand a regulator without anyone reassembling it by hand first.',
  },
];

/** Beyond HMDA, and what is coming. Marked honestly. */
const frontier = [
  {
    label: 'Shipped',
    title: 'Lending beyond HMDA',
    description:
      'A consumer trial-balance file — 82 fields — extends the same fair lending and CRA screens to installment and consumer lending, where most institutions have no analytical coverage at all because the data never had a reporting format to arrive in.',
  },
  {
    label: 'Roadmap',
    title: 'Call Reports and self-examination',
    description:
      'Call Report data is ingested today from the FDIC and drives peer benchmarking. Call Report preparation, and a guided self-examination workflow, are in active development inside the Enterprise tier.',
  },
  {
    label: 'Roadmap',
    title: 'BSA/AML program governance',
    description:
      'Policy hygiene, an obligation register with cadence diagnostics, and a risk assessment where deviation requires written justification. This is the governance layer above a monitoring engine — deliberately not a transaction-monitoring competitor.',
  },
  {
    label: 'Roadmap',
    title: 'Consumer demographic proxy',
    description:
      'Bayesian Improved Surname Geocoding implemented to the CFPB’s published methodology — probabilities rather than thresholds — so fair lending analysis extends to consumer portfolios where demographics are not collected.',
  },
];

/** The reproducibility argument — concrete claims only. The section an audit practice presses on. */
const reproducibility = [
  {
    icon: FingerPrintIcon,
    title: 'Every figure resolves to a source',
    description:
      "A tract income level traces to the Census reference record it came from. An 80%-of-AMI threshold traces to the county and the year it was drawn for. A qualification traces to the CFR section it rests on — and the citation follows the institution's own primary regulator rather than a default. Nothing on a Clarid output is a number without an address.",
  },
  {
    icon: ArrowPathIcon,
    title: 'The same inputs produce the same output',
    description:
      'AI performs detection; the compliance determination itself is a deterministic rules engine. The figure in a draft is the figure on the client call three weeks later, because nothing in between is a model deciding again. That separation is architectural, not a setting.',
  },
  {
    icon: ScaleIcon,
    title: 'The methodology is a control, not a credential',
    description:
      'The fair lending, CRA, and HMDA engine is built to specifications written by our partner Michael Wallace, and his validation fixtures run before and after every change to that engine. A regression in a screen is caught by the person who wrote the standard for it — which makes the expertise a control in the pipeline rather than a name on a masthead.',
  },
  {
    icon: DocumentMagnifyingGlassIcon,
    title: 'Independence is derived, not asserted',
    description:
      'Findings are re-derived from public reference data rather than accepted from the party that supplied them. In our securities validation work every affordability flag that arrives with a pool is independently recomputed from Census data for the year the pool was securitized, and any disagreement is surfaced loan by loan — so the validation never inherits the seller’s own conclusion. A second look built this way is defensible on its own terms.',
  },
];

/** Clarid's own partners. Credentials from docs/CROWE_RFI_RESPONSE.md and docs/DUE_DILIGENCE.md. */
const partners = [
  {
    name: 'Jason Willard',
    role: 'Former FDIC Supervisory Examiner',
    description:
      'Partner in Clarid. Supervisory examination experience on the FDIC side of the table informs how findings are framed, what an examiner will ask for next, and where an institution’s record tends to fall apart under review.',
  },
  {
    name: 'Michael Wallace',
    role: 'Former Federal Reserve Supervisory Examiner · Fair Lending SME · Wallace Consulting Co., LLC',
    description:
      'Partner in Clarid, with roughly three decades of fair lending practice at the Federal Reserve. He writes the specifications the fair lending, CRA, and HMDA engine is built to, and his fixtures gate every change to it. The methodology is not modeled on supervisory practice at a distance — it is written by someone who conducted it.',
  },
];

/** How we could work together. */
const paths = [
  {
    num: '01',
    label: 'Services',
    title: 'ABS runs the engagement on the platform',
    description:
      'The work is delivered under the ABS brand, by ABS consultants, to ABS clients. Clarid is the engine underneath and stays there. The client never has to learn a system, because the firm they hired is the one operating it.',
  },
  {
    num: '02',
    label: 'Channel',
    title: 'The institution licenses Clarid, ABS advises',
    description:
      'The bank or credit union holds the subscription and runs the platform itself; ABS makes the introduction and stays in the advisory seat — recommending the system rather than operating it.',
  },
  {
    num: '03',
    label: 'Build',
    title: 'ABS expertise becomes part of the product',
    description:
      'Where a practice has depth Clarid does not yet cover, that expertise can be specified into the platform itself rather than delivered alongside it. This is how the fair lending engine was built, and it is how the Call Report and self-examination work now underway is being built.',
  },
];

/* ─── Component ─── */
export default function ABSPartnerPage() {
  useEffect(() => {
    document.title = 'Advanced Business Solutions + Clarid — What Clarid Does, and How We Could Work Together';
    const metaTags: Record<string, string> = {
      'og:title': 'Advanced Business Solutions + Clarid',
      'og:description':
        'HMDA validation, fair lending screens, CRA lending and investment analysis, redlining geography, and exam packaging — reproducible, cited, and built to a former Federal Reserve fair lending SME\'s methodology.',
      'og:type': 'website',
      'og:image': '/clarid-logo.png',
      robots: 'noindex, nofollow',
    };
    const created: HTMLMetaElement[] = [];
    for (const [property, content] of Object.entries(metaTags)) {
      const meta = document.createElement('meta');
      meta.setAttribute(property === 'robots' ? 'name' : 'property', property);
      meta.setAttribute('content', content);
      document.head.appendChild(meta);
      created.push(meta);
    }
    return () => {
      created.forEach((t) => document.head.removeChild(t));
      document.title = 'Clarid — Clarity in every communication';
    };
  }, []);

  return (
    <div className="min-h-screen bg-white font-body text-slate-600">
      {/* ─── Header · lockup only, no nav, no CTA ─── */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 py-5 flex items-center">
          <div className="flex items-center gap-5">
            <img src="/clarid-logo.png" alt="Clarid" className="h-10" />
            <span className="text-slate-300 text-xl font-light">×</span>
            <span className="font-display text-2xl font-bold text-navy-900 tracking-tight">Advanced Business Solutions</span>
          </div>
        </div>
      </header>

      {/* ─── 1 · Hero ─── */}
      <section className="pt-36 pb-24 md:pt-48 md:pb-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 text-center">
          <FadeUp>
            <p className="text-base font-semibold tracking-[0.2em] uppercase text-teal-600 mb-8">
              Prepared for Advanced Business Solutions
            </p>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h1 className="text-5xl md:text-6xl lg:text-7xl xl:text-[5.25rem] font-display font-bold text-navy-900 leading-[1.1] mb-10 max-w-5xl mx-auto">
              What Clarid does, and how we could work together
            </h1>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="text-xl md:text-2xl lg:text-[1.6rem] text-slate-500 max-w-4xl mx-auto leading-relaxed">
              Clarid is an AI-native compliance platform for community banks and credit unions —
              HMDA validation, fair lending and redlining analysis, CRA lending and investment
              testing, and exam packaging, computed and cited rather than assembled by hand. This
              page is a plain account of what it does today, what is coming, who builds it, and the
              shapes a partnership could take.
            </p>
          </FadeUp>
          <FadeUp delay={0.3}>
            <p className="mt-12 text-base text-slate-400">
              Following our conversation with the ABS team.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ─── 2 · What Clarid does ─── */}
      <section className="py-24 md:py-36 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-6">
              What Clarid does
            </h2>
            <p className="text-lg text-slate-500 text-center max-w-4xl mx-auto mb-16 leading-relaxed">
              Everything in this section is running in production today.
            </p>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            {capabilities.map((c, i) => (
              <ScaleIn key={c.title} delay={i * 0.06}>
                <div className="group p-10 lg:p-12 bg-white rounded-2xl border border-slate-100 hover:border-teal-200 hover:shadow-xl transition-all duration-500 h-full">
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center group-hover:bg-teal-100 group-hover:scale-110 transition-all duration-300">
                      <c.icon className="h-6 w-6 text-teal-600" />
                    </div>
                    <p className="text-sm font-semibold tracking-[0.12em] uppercase text-teal-600">{c.area}</p>
                  </div>
                  <h3 className="text-2xl font-display font-bold text-navy-900 mb-4">{c.title}</h3>
                  <p className="text-lg text-slate-500 leading-relaxed">{c.description}</p>
                </div>
              </ScaleIn>
            ))}
          </div>
          <FadeUp delay={0.2}>
            <p className="mt-16 text-center text-xl md:text-[1.35rem] text-navy-900 font-semibold max-w-4xl mx-auto leading-relaxed">
              These are not six tools that export to each other. They are one connected record —
              a finding in marketing surfaces in the exam package without anyone reconciling
              spreadsheets to make it happen.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ─── 3 · Beyond HMDA and what's next ─── */}
      <section className="py-24 md:py-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-6">
              Where it is going
            </h2>
            <p className="text-lg text-slate-500 text-center max-w-4xl mx-auto mb-16 leading-relaxed">
              Marked honestly — shipped is shipped, roadmap is roadmap.
            </p>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10 max-w-6xl mx-auto">
            {frontier.map((f, i) => (
              <ScaleIn key={f.title} delay={i * 0.08}>
                <div className="p-10 lg:p-12 bg-slate-50 rounded-2xl border border-slate-100 h-full">
                  <span
                    className={
                      f.label === 'Shipped'
                        ? 'inline-block px-3 py-1 rounded-full text-xs font-semibold tracking-[0.1em] uppercase bg-teal-100 text-teal-700 mb-5'
                        : 'inline-block px-3 py-1 rounded-full text-xs font-semibold tracking-[0.1em] uppercase bg-slate-200 text-slate-600 mb-5'
                    }
                  >
                    {f.label}
                  </span>
                  <h3 className="text-2xl font-display font-bold text-navy-900 mb-4">{f.title}</h3>
                  <p className="text-lg text-slate-500 leading-relaxed">{f.description}</p>
                </div>
              </ScaleIn>
            ))}
          </div>
        </div>
      </section>

      {/* ─── 4 · Reproducibility ─── */}
      <section className="py-24 md:py-36 bg-navy-900 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '48px 48px' }}
        />
        <div className="relative max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <div className="text-center mb-14">
              <p className="text-base font-semibold tracking-[0.2em] uppercase text-teal-300 mb-5">Where did this number come from?</p>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-white mb-8">
                Every number has an address
              </h2>
              <p className="text-xl md:text-[1.35rem] text-slate-400 max-w-5xl mx-auto leading-relaxed">
                It is the question that decides whether a finding survives a client's pushback or a
                regulator's review, and it is the question a platform has to answer before it is worth
                putting underneath someone else's professional opinion. Four concrete answers.
              </p>
            </div>
          </FadeUp>
          <div className="space-y-6 lg:space-y-8 mt-14">
            {reproducibility.map((r, i) => (
              <ScaleIn key={r.title} delay={i * 0.08}>
                <div className="p-10 lg:p-12 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm hover:bg-white/[0.08] hover:border-white/20 transition-all duration-500">
                  <div className="flex items-center gap-4 mb-5">
                    <r.icon className="h-7 w-7 text-teal-400 flex-shrink-0" />
                    <h3 className="text-2xl font-display font-bold text-white">{r.title}</h3>
                  </div>
                  <p className="text-lg text-slate-400 leading-relaxed">{r.description}</p>
                </div>
              </ScaleIn>
            ))}
          </div>
          <FadeUp delay={0.3}>
            <p className="mt-14 text-center text-lg text-slate-400 max-w-4xl mx-auto leading-relaxed">
              Underneath all four: an append-only, SHA-256 hash-chained audit trail. Every check,
              finding, approval, and override is recorded and attributable, and the exports are
              hash-verified.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ─── 5 · Who builds it ─── */}
      <section className="py-24 md:py-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-6">
                Who builds it
              </h2>
              <p className="text-xl md:text-[1.35rem] text-slate-500 text-center max-w-4xl mx-auto mb-16 leading-relaxed">
                Clarid is founder-led, with two former supervisory examiners as equity partners —
                not contractors, and not an advisory board.
              </p>
            </FadeUp>
            <div className="space-y-6 lg:space-y-8">
              {partners.map((p, i) => (
                <ScaleIn key={p.name} delay={i * 0.1}>
                  <div className="p-10 lg:p-12 bg-slate-50 rounded-2xl border border-slate-100 hover:border-teal-200 hover:shadow-xl transition-all duration-500">
                    <div className="flex items-start gap-5 mb-4">
                      <ShieldCheckIcon className="h-8 w-8 text-teal-600 flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="text-2xl md:text-3xl font-display font-bold text-navy-900">{p.name}</h3>
                        <p className="text-base text-teal-700 font-semibold mt-1.5">{p.role}</p>
                      </div>
                    </div>
                    <p className="text-lg text-slate-500 leading-relaxed">{p.description}</p>
                  </div>
                </ScaleIn>
              ))}
            </div>
            <FadeUp delay={0.3}>
              <p className="mt-14 text-center text-lg text-slate-500 max-w-4xl mx-auto leading-relaxed">
                Between them, both sides of the supervisory table — FDIC and the Federal Reserve —
                full-time on the product rather than on a masthead.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ─── 6 · How we could work together ─── */}
      <section className="py-24 md:py-36 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-6">
              How we could work together
            </h2>
            <p className="text-xl md:text-[1.35rem] text-slate-500 text-center max-w-4xl mx-auto mb-16 leading-relaxed">
              Three shapes, and they are not exclusive — firms tend to run more than one, because
              different clients want different things from the same practice.
            </p>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
            {paths.map((p, i) => (
              <ScaleIn key={p.num} delay={i * 0.1}>
                <div className="p-10 lg:p-12 bg-white rounded-2xl border border-slate-100 hover:border-teal-200 hover:shadow-xl transition-all duration-500 h-full">
                  <div className="flex items-center gap-4 mb-5">
                    <span className="text-2xl font-display font-bold text-navy-900/20">{p.num}</span>
                    <p className="text-sm font-semibold tracking-[0.14em] uppercase text-teal-600">{p.label}</p>
                  </div>
                  <h3 className="text-2xl font-display font-bold text-navy-900 mb-4">{p.title}</h3>
                  <p className="text-lg text-slate-500 leading-relaxed">{p.description}</p>
                </div>
              </ScaleIn>
            ))}
          </div>
          <FadeUp delay={0.3}>
            <p className="mt-14 text-center text-lg text-slate-500 max-w-4xl mx-auto leading-relaxed">
              The first two models are running today with an advisory firm we partner with, so
              neither is theoretical — the mechanics, the delivery, and the economics have already
              been worked out once.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ─── 7 · Close · quiet, no CTA ─── */}
      <section className="py-24 md:py-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl font-display font-bold text-navy-900 mb-10">
                A reasonable next step
              </h2>
            </FadeUp>
            <FadeUp delay={0.1}>
              <div className="space-y-7">
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  This page is not a proposal, and there is nothing to sign at the bottom of it. It
                  exists so the whole team has the same picture of what the platform does and where
                  the overlap with ABS actually sits.
                </p>
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  If it is useful, the most productive next step is usually the least formal one: a
                  working session on a real file, where the output either holds up against what a
                  consultant would have produced by hand or it does not. That answers more in an hour
                  than a deck answers in a month.
                </p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ─── 8 · Contact ─── */}
      <section className="py-24 md:py-32 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 text-center">
          <FadeIn>
            <div className="space-y-3">
              <p className="text-3xl font-display font-bold text-navy-900">Bobby Sloan</p>
              <p className="text-lg text-slate-500">Founder &amp; CEO, Clarid</p>
              <div className="flex items-center justify-center gap-3 mt-4 text-lg text-teal-600">
                <EnvelopeIcon className="h-5 w-5" />
                <a href="mailto:bobby@clarid.ai" className="hover:text-teal-700 transition-colors">bobby@clarid.ai</a>
              </div>
              <p className="text-base text-slate-400 mt-1">clarid.ai</p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <footer className="py-8 border-t border-slate-100">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3 text-slate-400">
            <img src="/clarid-logo.png" alt="Clarid" className="h-7 opacity-40" />
            <span className="text-slate-200">×</span>
            <span className="font-display font-semibold text-sm">Advanced Business Solutions</span>
          </div>
          <p className="text-slate-400 text-sm">
            &copy; {new Date().getFullYear()} Clarid. Confidential — prepared for Advanced Business Solutions.
          </p>
          <div className="flex items-center gap-4 text-sm">
            <a href="/security" className="text-slate-400 hover:text-teal-600 transition-colors">Security</a>
            <a href="/privacy" className="text-slate-400 hover:text-teal-600 transition-colors">Privacy</a>
          </div>
        </div>
      </footer>

      <div className="sr-only">
        Prepared by Clarid for Advanced Business Solutions. This page describes a possible partnership
        from Clarid's perspective and does not imply an existing relationship with, or endorsement by,
        Advanced Business Solutions.
      </div>
    </div>
  );
}
